import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { ProjectRenderer } from './ProjectRenderer';
import { OrbitControls } from './controls/OrbitControls';
import { debounce } from 'lodash';
import { ThreeEvent } from './types';
import { PerspectiveCamera, Box3, Vector3 } from 'three';
import gsap from 'gsap';

export class CameraController {
	camera!: PerspectiveCamera;
	screenshotCamera!: PerspectiveCamera;
	controls!: OrbitControls;

	constructor(private renderer: ProjectRenderer) {
		this.init();
	}

	private init() {
		this.camera = new PerspectiveCamera();
		this.screenshotCamera = new PerspectiveCamera();

		this.camera.position.set(0, 1, 2);
		this.camera.layers.enableAll();
		this.screenshotCamera.layers.enableAll();

		this.camera.aspect =
			this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;
		this.screenshotCamera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.screenshotCamera.updateProjectionMatrix();

		this.createControls();
	}

	private createControls() {
		this.controls = new OrbitControls(this.camera, this.renderer.canvas);
		this.controls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;

		this.controls.minDistance = RENDERER_CONFIG.CONTROLS.MIN_DISTANCE;
		this.controls.maxDistance = RENDERER_CONFIG.CONTROLS.MAX_DISTANCE;

		const updateStateDistance = () => {
			const distance = this.camera.position.distanceTo(this.controls.target);
			this.renderer.emitter.emit(ThreeEvent.UPDATE_CAMERA_DISTANCE, distance);
		};

		updateStateDistance();

		this.controls.addEventListener(
			'change',
			debounce(updateStateDistance, 150)
		);

		this.controls.saveState();
	}

	prepareScreenshotCamera() {
		const box = new Box3().setFromObject(this.renderer.product);
		const center = box.getCenter(new Vector3());
		const size = box.getSize(new Vector3());

		this.screenshotCamera.position.set(
			0,
			size.length() * 1.2,
			size.length() * 1.2
		);

		this.screenshotCamera.lookAt(center);
		this.screenshotCamera.updateProjectionMatrix();
	}

	centerCamera() {
		this.controls.enabled = false;

		const box = new Box3().setFromObject(this.renderer.product);
		const size = new Vector3();
		box.getSize(size);

		const center = box.getCenter(new Vector3());
		this.controls.target.copy(center);

		const width = Math.abs(box.max.x - box.min.x),
			height = Math.abs(box.max.y - box.min.y),
			depth = Math.abs(box.max.z - box.min.z);

		const x = 0,
			y = height,
			z = width * 1.2;

		gsap.to(this.camera.position, {
			ease: 'power2.out',
			duration: 1,
			x,
			y,
			z,
			onUpdate: () => {
				this.controls.update();
			},
			onComplete: () => {
				this.controls.enabled = true;
			},
		});
	}

	setDistanceToTarget(distance: number) {
		const target = this.controls.target.clone();
		const currentPosition = this.camera.position.clone();
		const direction = new Vector3()
			.subVectors(currentPosition, target)
			.normalize();

		const newPosition = target
			.clone()
			.add(direction.clone().multiplyScalar(distance));

		gsap.to(this.camera.position, {
			x: newPosition.x,
			y: newPosition.y,
			z: newPosition.z,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.camera.lookAt(target);
			},
			onComplete: () => {
				this.controls.update();
			},
		});
	}

	handleResize(aspect: number) {
		this.camera.aspect = aspect;
		this.camera.updateProjectionMatrix();
	}

	dispose() {
		this.controls.dispose();
	}
}
